import { effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { UserStore } from '@shared/data';
import { KonfigurationenService } from 'lion-api';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MenuItemService {
  items: WritableSignal<MenuItem[]> = signal([]);
  isRefiVisibleForVr = signal(false);
  isEvaluationVisibleForFk = signal(false);

  private translateService = inject(TranslateService);
  private userStore = inject(UserStore);
  private konfigurationService = inject(KonfigurationenService);
  private router = inject(Router);

  constructor() {
    this.buildNavigation();
    effect(() => {
      const isRefiVisible = this.isRefiVisibleForVr();
      const isEvaluationVisible = this.isEvaluationVisibleForFk();

      const menuItems: MenuItem[] = [
        {
          id: 'information',
          label: this.translateService.instant('shared.route-title.information'),
          icon: 'pi pi-pw pi-info-circle',
          routerLink: '/information',
          active: false,
          visible: this.userStore.hasPermitAll()
        },
        {
          id: 'investment',
          label: this.translateService.instant('shared.route-title.investment'),
          icon: 'pi pi-fw lion-operative-anlage',
          routerLink: '/investment',
          active: false,
          visible: this.userStore.hasInvestmentPermission() || this.userStore.hasCorporatesInvestmentPermission()
        },
        {
          id: 'investment100',
          label: this.translateService.instant('shared.route-title.investment100'),
          icon: 'pi pi-fw lion-interbankeneinlage',
          routerLink: '/investment100',
          active: false,
          visible: this.userStore.hasInvestment100Permission()
        },
        {
          id: 'raising',
          label: this.translateService.instant('shared.route-title.raising'),
          icon: 'pi pi-fw lion-termingeld-aufnahme',
          routerLink: '/raising',
          active: false,
          visible: this.userStore.hasRaisingPermission()
        },
        {
          id: 'refi',
          label: this.translateService.instant('shared.route-title.refi'),
          icon: 'pi pi-fw lion-refinanzierungsdarlehen',
          routerLink: '/refi',
          active: false,
          visible:
            (this.userStore.hasRefiPermission() && this.userStore.isDzBankUser()) ||
            (this.userStore.hasRefiPermission() && !this.userStore.isDzBankUser() && isRefiVisible)
        },
        {
          id: 'cancellation',
          label: this.translateService.instant('shared.route-title.cancellation'),
          icon: 'pi pi-fw pi-undo',
          routerLink: '/cancellation',
          active: false,
          visible: this.userStore.hasCancellationPermission() || this.userStore.hasCorporatesCancellationPermission()
        },
        {
          id: 'trades-overview',
          label: this.translateService.instant('shared.route-title.business-status'),
          icon: 'pi pi-fw pi-history',
          routerLink: '/trades-overview',
          active: false,
          visible: this.userStore.hasBusinessStatusPermission() || this.userStore.hasCorporatesBusinessStatusPermission()
        },
        {
          id: 'limit',
          label: this.translateService.instant('shared.route-title.limit'),
          icon: 'pi pi-fw pi-wallet',
          routerLink: '/limit',
          active: false,
          visible: this.userStore.hasLimitPermission()
        },
        {
          id: 'evaluation',
          label: this.translateService.instant('shared.route-title.evaluation'),
          icon: 'pi pi-fw pi-search',
          routerLink: '/evaluation',
          active: false,
          visible:
            this.userStore.hasEvaluationPermission() ||
            this.userStore.hasCorporatesDzEvaluationPermission() ||
            (this.userStore.hasCorporatesExtEvaluationPermission() && isEvaluationVisible)
        },
        {
          id: 'admin',
          label: this.translateService.instant('shared.route-title.administration'),
          icon: 'pi pi-fw pi-cog',
          routerLink: '/admin',
          active: false,
          visible: this.userStore.hasAdminPermission() || this.userStore.hasCorporatesAdminPermission()
        },
        {
          id: 'margin',
          label: this.translateService.instant('shared.route-title.margin'),
          icon: 'pi pi-fw pi-percentage',
          routerLink: '/margin',
          active: false,
          visible: this.userStore.hasMarginPermission() || this.userStore.hasCorporatesMarginPermission()
        },
        {
          id: 'maturity',
          label: this.translateService.instant('shared.route-title.maturity'),
          icon: 'pi pi-fw pi-calendar-times',
          routerLink: '/maturity',
          active: false,
          visible: this.userStore.hasMaturityPermission() || this.userStore.hasCorporatesMaturityPermission()
        }
      ];
      this.items.set(this.setActiveItemsForUrl(menuItems, this.router.url));
    });
  }

  public buildNavigation(): void {
    this.konfigurationService.konfigurationGet('LIO_REFI_FUER_VR_ANZEIGEN').subscribe((response) => {
      const val = response.konfiguration?.value?.toLowerCase();
      this.isRefiVisibleForVr.set(val === '1' || val === 'true');
    });
    this.konfigurationService.konfigurationGet('LIO_EVALUATION_FUER_FK_ANZEIGEN').subscribe((response) => {
      const val = response.konfiguration?.value?.toLowerCase();
      this.isEvaluationVisibleForFk.set(val === '1' || val === 'true');
    });
  }

  public setActiveItem(url: string): void {
    this.items.update((items) => this.setActiveItemsForUrl(items, url));
  }

  private setActiveItemsForUrl(items: MenuItem[], url: string): MenuItem[] {
    return items.map((item) => {
      const link = item.routerLink;
      let active = false;
      if (link && link.length > 1) {
        const firstEntry = url.split('/')[1];
        active = firstEntry === link.split('/')[1];
      } else {
        active = url === '/';
      }
      return { ...item, active };
    });
  }
}
